/**
 * Minimal Server-Sent-Events line parser.
 *
 * The coach stream is consumed via fetch + ReadableStream (EventSource can't
 * POST), so we parse the wire format ourselves. `feedSSE` is a pure function:
 * hand it the leftover buffer plus a freshly-decoded chunk and it returns any
 * complete events plus the new leftover buffer. Kept DOM-free for unit testing.
 */

export interface SSEEvent {
  event: string;
  data: string;
}

/**
 * Append `chunk` to `buffer`, split off any complete events (delimited by a
 * blank line), and return them with the remaining partial buffer. Each event's
 * `event:` defaults to `message`; multiple `data:` lines are joined with `\n`.
 */
export function feedSSE(buffer: string, chunk: string): { buffer: string; events: SSEEvent[] } {
  let buf = buffer + chunk;
  const events: SSEEvent[] = [];

  let sep: number;
  // Records are separated by a blank line. Tolerate CRLF as well as LF.
  while ((sep = indexOfBlankLine(buf)) !== -1) {
    const raw = buf.slice(0, sep);
    buf = buf.slice(sep + blankLineLength(buf, sep));

    let event = 'message';
    const dataLines: string[] = [];
    for (const line of raw.split(/\r?\n/)) {
      if (line.startsWith('event:')) event = line.slice(6).trimStart();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
      // Lines that are blank, comments (`:`), or unknown fields are ignored.
    }
    if (dataLines.length > 0 || event !== 'message') {
      events.push({ event, data: dataLines.join('\n') });
    }
  }

  return { buffer: buf, events };
}

/** Index of the blank-line separator (`\n\n` or `\r\n\r\n`), or -1. */
function indexOfBlankLine(s: string): number {
  const lf = s.indexOf('\n\n');
  const crlf = s.indexOf('\r\n\r\n');
  if (lf === -1) return crlf;
  if (crlf === -1) return lf;
  return Math.min(lf, crlf);
}

function blankLineLength(s: string, at: number): number {
  return s.startsWith('\r\n\r\n', at) ? 4 : 2;
}
